'use client';

import { c as _c } from "react/compiler-runtime";
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { ChevronDownIcon, MenuIcon } from 'lucide-react';
import Link from 'next/link';
import * as React from 'react';
import { ScrollArea } from '@/components/common/scroll-area';
import { getSiteConfig, isUkSite } from '@/lib/config/site';
import { getBrandName } from '@/lib/utils/brand';
import type { SITE_SETTINGS_QUERY_RESULT } from '@/sanity/types';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../common/accordion';
import { Button } from '../../../common/button';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '../../../common/sheet';
import CalendlyFlowPopup from '../../../features/Calendly/CalendlyFlowPopup';
import RegisterInterestForm from '../../../features/BlockContent/HeroSliderBanner/RegisterInterest';
type MastheadNavigationType = NonNullable<SITE_SETTINGS_QUERY_RESULT>['mastheadNavigation'];
type NavigationItem = MastheadNavigationType['navigation'][number];
type ProviderProps = {
  navigation?: NavigationItem[];
  mastheadNavigation?: MastheadNavigationType;
  careers?: boolean;
  variant?: string;
};
const MobileNavigationContext = React.createContext<{
  close: () => void;
} | null>(null);
function useMobileNavigation() {
  const context = React.useContext(MobileNavigationContext);
  if (!context) {
    throw new Error("useMobileNavigation must be used within a MastHeadMobileProvider");
  }
  return context;
}
export default function MastHeadMobileProvider(t0) {
  const $ = _c(31);
  const {
    navigation,
    mastheadNavigation,
    careers,
    variant
  } = t0;
  const [open, setOpen] = React.useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setOpen(false);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const close = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      close
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = variant === "careers" ? "size-8 text-careers-blue-darker" : "size-8 text-white";
  let t4;
  if ($[2] !== t3) {
    t4 = <SheetTrigger className="p-4 align-bottom" aria-label="Navigation Menu"><MenuIcon className={t3} /></SheetTrigger>;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <SheetHeader><SheetTitle className="sr-only">{`${getBrandName()} Navigation Menu`}</SheetTitle><SheetDescription className="sr-only">Use this to navigate around our site</SheetDescription></SheetHeader>;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== navigation) {
    t6 = navigation ?? [];
    $[5] = navigation;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t6) {
    t7 = t6.map(_temp);
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== mastheadNavigation) {
    t8 = mastheadNavigation?.showContactUs && mastheadNavigation?.contactForm && <MobileContactButton contactForm={mastheadNavigation.contactForm} />;
    $[9] = mastheadNavigation;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== careers || $[12] !== mastheadNavigation?.showCalendlyFlow) {
    t9 = mastheadNavigation?.showCalendlyFlow && !careers && <CalendlyFlowPopup><Button size="sm" variant="inverse" className="rounded-full font-bold w-full">Enquire today</Button></CalendlyFlowPopup>;
    $[11] = careers;
    $[12] = mastheadNavigation?.showCalendlyFlow;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== mastheadNavigation?.showFamilyHub) {
    t10 = mastheadNavigation?.showFamilyHub && <MobileHubButton />;
    $[14] = mastheadNavigation?.showFamilyHub;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== careers || $[17] !== mastheadNavigation?.showCareers) {
    t11 = mastheadNavigation?.showCareers && <MobileCareersButton careers={careers} />;
    $[16] = careers;
    $[17] = mastheadNavigation?.showCareers;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t10 || $[20] !== t11 || $[21] !== t8 || $[22] !== t9) {
    t12 = <div className="flex flex-col gap-4 mt-6">{t8}{t9}{t10}{t11}</div>;
    $[19] = t10;
    $[20] = t11;
    $[21] = t8;
    $[22] = t9;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t12 || $[25] !== t7) {
    t13 = <SheetContent className="border-0 bg-primary-blue text-white" side="left"><ScrollArea className="h-dvh p-5">{t5}<div className="flex flex-col gap-4 text-lg font-medium">{t7}{t12}</div></ScrollArea></SheetContent>;
    $[24] = t12;
    $[25] = t7;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== open || $[28] !== t13 || $[29] !== t4) {
    t14 = <MobileNavigationContext.Provider value={t2}><Sheet open={open} onOpenChange={setOpen}>{t4}{t13}</Sheet></MobileNavigationContext.Provider>;
    $[27] = open;
    $[28] = t13;
    $[29] = t4;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  return t14;
}
function _temp(item, index) {
  const itemKey = item._id || `mobile-nav-${item._type ?? "item"}-${item.title ?? ""}-${index}`;
  return <MobileNavItem key={itemKey} item={item} itemKey={itemKey} />;
}
function MobileNavItem(t0) {
  const $ = _c(20);
  const {
    item,
    itemKey
  } = t0;
  if (item._type === "navigationMenu") {
    const hasLink = Boolean(item.link?.url);
    let t1;
    if ($[0] !== hasLink || $[1] !== item.link?.url || $[2] !== item.title) {
      t1 = hasLink ? <AccordionPrimitive.Header className="flex"><div className="flex w-full items-center"><MobileLink href={item.link?.url || ""} className="flex-1 py-4"><span className="text-lg font-medium hover:text-primary-pink">{item.title}</span></MobileLink><AccordionPrimitive.Trigger className="p-4 text-muted-foreground [&[data-state=open]>svg]:rotate-180"><ChevronDownIcon className="size-4 transition-transform duration-200" /></AccordionPrimitive.Trigger></div></AccordionPrimitive.Header> : <AccordionTrigger className="text-lg font-medium hover:text-primary-pink data-[state=open]:text-primary-pink">{item.title}</AccordionTrigger>;
      $[0] = hasLink;
      $[1] = item.link?.url;
      $[2] = item.title;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    let t2;
    if ($[4] !== item.items || $[5] !== itemKey) {
      t2 = item.items && item.items.length > 0 && <AccordionContent className="flex flex-col gap-2 border-y py-4 pl-4">{item.items.map((navItem, navIndex) => {
          const subItemKey = navItem._id || `sub-${itemKey}-${navItem.title ?? ""}-${navIndex}`;
          return <MobileLink key={subItemKey} href={navItem.url || ""}><span className="text-lg font-medium hover:text-primary-pink">{navItem.title}</span></MobileLink>;
        })}</AccordionContent>;
      $[4] = item.items;
      $[5] = itemKey;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    let t3;
    if ($[7] !== itemKey || $[8] !== t1 || $[9] !== t2) {
      t3 = <AccordionItem value={itemKey}>{t1}{t2}</AccordionItem>;
      $[7] = itemKey;
      $[8] = t1;
      $[9] = t2;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    let t4;
    if ($[11] !== itemKey || $[12] !== t3) {
      t4 = <Accordion key={itemKey} type="single" collapsible={true}>{t3}</Accordion>;
      $[11] = itemKey;
      $[12] = t3;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    return t4;
  }
  const t1 = item.url || "";
  let t2;
  if ($[14] !== item.title) {
    t2 = <span className="hover:text-primary-pink">{item.title}</span>;
    $[14] = item.title;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  let t3;
  if ($[16] !== itemKey || $[17] !== t1 || $[18] !== t2) {
    t3 = <MobileLink key={itemKey} href={t1}>{t2}</MobileLink>;
    $[16] = itemKey;
    $[17] = t1;
    $[18] = t2;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  return t3;
}
function MobileLink(t0) {
  const $ = _c(5);
  const {
    href,
    children,
    className
  } = t0;
  const {
    close
  } = useMobileNavigation();
  const t1 = className ?? "text-lg font-medium";
  let t2;
  if ($[0] !== children || $[1] !== close || $[2] !== href || $[3] !== t1) {
    t2 = <Link href={href} className={t1} onClick={close}>{children}</Link>;
    $[0] = children;
    $[1] = close;
    $[2] = href;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function MobileHubLink() {
  const $ = _c(2);
  const {
    close
  } = useMobileNavigation();
  let t0;
  if ($[0] !== close) {
    t0 = <Link href="/the-family-hub/" className="hover:text-primary-pink text-lg font-medium" onClick={close}>The Family Hub</Link>;
    $[0] = close;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function MobileHubButton() {
  const $ = _c(2);
  const {
    close
  } = useMobileNavigation();
  let t0;
  if ($[0] !== close) {
    t0 = <Button size="sm" variant="inverse" className="rounded-full font-bold w-full" asChild={true}><Link href="/the-family-hub/" onClick={close}>The Family Hub</Link></Button>;
    $[0] = close;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function MobileContactButton(t0) {
  const $ = _c(6);
  const {
    contactForm
  } = t0;
  const t1 = contactForm.formTitle || "";
  const t2 = contactForm.formSubTitle || "";
  const t3 = contactForm.ctaTitle || "Get in touch";
  let t4;
  if ($[0] !== t3) {
    t4 = <Button size="sm" variant="inverse" className="rounded-full font-bold w-full">{t3}</Button>;
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== t1 || $[3] !== t2 || $[4] !== t4) {
    t5 = <RegisterInterestForm formTitle={t1} formDescription={t2}>{t4}</RegisterInterestForm>;
    $[2] = t1;
    $[3] = t2;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  return t5;
}
function MobileCareersButton(t0) {
  const $ = _c(12);
  const {
    careers
  } = t0;
  const {
    close
  } = useMobileNavigation();
  if (isUkSite() && !careers) {
    let t1;
    if ($[0] !== close) {
      t1 = <Button size="sm" variant="green" className="rounded-full font-bold w-full" asChild={true}><a href="https://www.nfamilycareers.com/" onClick={close}>Careers at N</a></Button>;
      $[0] = close;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  const t1 = careers ? "/" : "/careers";
  const t2 = careers ? "N Family" : "Careers at N";
  let t3;
  if ($[2] !== close || $[3] !== t1 || $[4] !== t2) {
    t3 = <Button size="sm" variant="green" className="rounded-full font-bold w-full" asChild={true}><Link href={t1} onClick={close}>{t2}</Link></Button>;
    $[2] = close;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== careers || $[7] !== close) {
    t4 = careers && <Button size="sm" variant="inverse" className="rounded-full font-bold w-full" asChild={true}><Link href={getSiteConfig().pinpoint.registerInterestUrl} target="_blank" rel="noopener noreferrer" onClick={close}>Register Interest</Link></Button>;
    $[6] = careers;
    $[7] = close;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}